/**
 * App shell: a title bar and a floating bottom tab bar, each tab in its own
 * colours so a vendor knows where they are without reading.
 *
 * Bottom navigation, not a drawer. On a 6-inch phone held one-handed, the top
 * of the screen is the hardest place to reach and a hamburger hides the two
 * things a vendor opens the app for — their jobs and their money.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { Icon, type IconName } from './Icon';
import { Photo } from './Photo';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';

const MEDIA = ['radio', 'newspaper', 'digital_screen', 'cinema', 'tv_cable', 'event_venue'];
/** Has sites or slots to sell: bookings and listings. */
export const ownsMedia = (caps: string[]) =>
  caps.some((c) => (c.startsWith('ooh_') && c !== 'ooh_mobile_van') || MEDIA.includes(c));
/** Does jobs for customers (printing, fabrication, installing…): jobs and orders. */
export const doesWork = (caps: string[]) =>
  caps.some(
    (c) =>
      c !== 'creator' &&
      !((c.startsWith('ooh_') && c !== 'ooh_mobile_van') || MEDIA.includes(c)),
  );
/** Sells their own content as a creator / influencer: media kit, packages, deals. */
export const isCreator = (caps: string[]) => caps.includes('creator');
/** Only a creator: the vendor tabs (jobs, sites) would be dead ends for them. */
export const creatorOnly = (caps: string[]) =>
  isCreator(caps) && !ownsMedia(caps) && !doesWork(caps);

/** Which tab's colours a page wears (index.css [data-sec]). */
export function sectionOf(path: string): string {
  if (path.startsWith('/jobs')) return 'jobs';
  if (path.startsWith('/orders')) return 'orders';
  if (path.startsWith('/bookings')) return 'bookings';
  if (path.startsWith('/listings')) return 'listings';
  if (path.startsWith('/payouts')) return 'payouts';
  if (path.startsWith('/creator/packages')) return 'packages';
  if (path.startsWith('/creator')) return 'creator';
  if (path.startsWith('/deals')) return 'deals';
  if (path.startsWith('/opportunities')) return 'opps';
  return 'profile';
}

/**
 * Unread lines across a creator's deals, for the Deals tab badge: read once
 * a minute while the app is visible, and again on every page change. A
 * failure just shows no badge.
 */
function useDealUnread(enabled: boolean, pathname: string): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .get<{ unread?: number }>('/v1/partner/creator/deals/unread')
        .then((r) => alive && setN(typeof r.unread === 'number' ? r.unread : 0))
        .catch(() => alive && setN(0));
    };
    load();
    const t = window.setInterval(load, 60_000);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', load);
    };
  }, [enabled, pathname]);
  return n;
}

/**
 * New campaigns that fit this creator, plus brand decisions on their
 * applications not yet seen: the Opportunities badge. Same rhythm as the
 * deals badge; a failure shows none.
 */
function useOpportunityCount(enabled: boolean, pathname: string): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .get<{ total?: number }>('/v1/partner/creator/opportunities/count')
        .then((r) => alive && setN(typeof r.total === 'number' ? r.total : 0))
        .catch(() => alive && setN(0));
    };
    load();
    const t = window.setInterval(load, 60_000);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', load);
    };
  }, [enabled, pathname]);
  return n;
}

export function Layout() {
  const { t } = useI18n();
  const T = useT();
  const { vendor, login } = useSession();
  const { pathname } = useLocation();

  // A media owner sees bookings and sites; a printer sees jobs and orders.
  // Neither gets tabs that would be a dead end they tap once and never again.
  const caps = vendor?.capabilities ?? [];
  const media = ownsMedia(caps);
  const creator = isCreator(caps);
  const unread = useDealUnread(creator, pathname);
  const opps = useOpportunityCount(creator, pathname);
  // A site owner that prints its own sites gets those jobs, so it sees them too.
  const work = doesWork(caps) || (!media && !creator) || Boolean(vendor?.printsOwnSites);
  // A creator who is nothing else gets only creator tabs. Earnings is the
  // same payouts page every vendor has: one ledger, one TDS story.
  const tabs: Array<{ to: string; label: string; icon: IconName; end?: boolean }> =
    creatorOnly(caps) && !vendor?.printsOwnSites
      ? [
          { to: '/creator', label: t('nav.creatorHome'), icon: 'sparkle', end: true },
          { to: '/creator/kit', label: t('nav.mediaKit'), icon: 'star' },
          { to: '/creator/packages', label: t('nav.packages'), icon: 'tag' },
          { to: '/deals', label: t('nav.deals'), icon: 'jobs' },
          { to: '/opportunities', label: T('मौके', 'Campaigns'), icon: 'globe' },
          { to: '/payouts', label: t('nav.earnings'), icon: 'payouts' },
        ]
      : [
          ...(work
            ? [
                { to: '/jobs', label: t('nav.jobs'), icon: 'jobs' as const },
                { to: '/orders', label: T('ऑर्डर', 'Orders'), icon: 'orders' as const },
              ]
            : []),
          ...(media
            ? [
                { to: '/bookings', label: T('बुकिंग', 'Bookings'), icon: 'bookings' as const },
                { to: '/listings', label: t('nav.listings'), icon: 'listings' as const },
              ]
            : []),
          // A vendor who is also a creator gets one tab into the creator side.
          ...(creator
            ? [{ to: '/creator', label: t('nav.creator'), icon: 'sparkle' as const }]
            : []),
          { to: '/payouts', label: t('nav.payouts'), icon: 'payouts' },
          { to: '/profile', label: t('nav.profile'), icon: 'profile' },
        ];

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col" data-sec={sectionOf(pathname)}>
      <header className="glass sticky top-0 z-20 border-b border-white/60 px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <img
              src={`${import.meta.env.BASE_URL}lulu-logo.svg`}
              alt=""
              className="h-9 w-9 shrink-0 rounded-xl"
            />
            <span className="min-w-0 leading-tight">
              <span className="block font-display text-[15px] font-bold tracking-tight">
                {t('app.name')}
              </span>
              {vendor && (
                <span className="block truncate text-xs text-muted">{vendor.businessName}</span>
              )}
            </span>
          </Link>
          {vendor && (
            <Link to="/profile" aria-label={t('nav.profile')} className="shrink-0">
              <Photo
                name={login?.name || vendor.businessName}
                photoAt={login?.photoAt}
                className="h-10 w-10 text-sm"
              />
            </Link>
          )}
        </div>
      </header>

      <main key={sectionOf(pathname)} className="rise flex-1 px-4 pb-28 pt-4">
        <Outlet />
      </main>

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-20 mx-auto max-w-md px-3">
        <ul className="glass flex rounded-[26px] border border-white/70 p-1.5 shadow-[0_18px_40px_-16px_rgba(23,32,51,.35)]">
          {tabs.map((tab) => (
            <li key={tab.to} className="flex-1" data-sec={sectionOf(tab.to)}>
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `flex min-h-[54px] flex-col items-center justify-center gap-0.5 rounded-[20px] px-1 text-[11px] transition ${
                    isActive ? 'font-bold text-white shadow-lift' : 'font-medium text-muted'
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        backgroundImage:
                          'linear-gradient(135deg, var(--sec-from), var(--sec-to))',
                      }
                    : undefined
                }
              >
                <span className="relative">
                  <Icon name={tab.icon} className="h-[22px] w-[22px]" />
                  {(() => {
                    // Deals: unread lines. Campaigns: new opportunities and decisions.
                    // A vendor who is also a creator sees both on the one Creator tab.
                    const n =
                      tab.to === '/deals'
                        ? unread
                        : tab.to === '/opportunities'
                          ? opps
                          : tab.to === '/creator' && !tab.end
                            ? unread + opps
                            : 0;
                    return n > 0 ? (
                      <span
                        className="absolute -right-2.5 -top-1.5 min-w-[18px] rounded-full bg-rose-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white"
                        aria-label={`${n} new`}
                      >
                        {n > 9 ? '9+' : n}
                      </span>
                    ) : null;
                  })()}
                </span>
                <span className="truncate">{tab.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
